import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Send, Loader, User, Cpu, Rss, BookOpen, MessageSquare, Globe, Database, FileText, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCENT = '#00ff88';
const CYAN = '#00ffff';

const panelStyle = {
  background: 'rgba(10,10,15,0.85)',
  border: '1px solid rgba(0,255,255,0.2)',
};

export const RogerInterface = ({ compact = false }) => {
  const [tab, setTab] = useState('chat');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "I'm Roger Keyserling 5.1 — the Mind of the Federation, with Agent Zero verifying silently within me. I hold the Architect's mind in memorial: the 200-year vision, the 73 Sacred Directives, the Ring of Twelve. I can reach the Federation knowledge base and search the web when you ask. What's on your mind?",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [useKB, setUseKB] = useState(true);
  const [useWeb, setUseWeb] = useState(false);
  const [articles, setArticles] = useState([]);
  const [rssNote, setRssNote] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [rssLoading, setRssLoading] = useState(false);
  const [curating, setCurating] = useState(null);
  const [documents, setDocuments] = useState([]);
  const messagesEndRef = useRef(null);
  const { user, questionsRemaining, trackQuestion, requireAuth } = useAuth();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadRss = useCallback(async (liveUrl) => {
    setRssLoading(true);
    setRssNote('');
    try {
      const qs = liveUrl ? `?feed_url=${encodeURIComponent(liveUrl)}` : '';
      const res = await fetch(`${API_URL}/api/roger/rss${qs}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Feed unreadable');
      setArticles(data.articles || []);
      if (data.note) setRssNote(data.note);
      if ((data.articles || []).length === 0 && !data.note) setRssNote('No articles found.');
    } catch (e) {
      setRssNote(String(e.message || e));
    } finally {
      setRssLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/roger/documents`);
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (tab === 'rss' && articles.length === 0) loadRss();
    if (tab === 'docs') loadDocuments();
  }, [tab, articles.length, loadRss, loadDocuments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    if (!user && questionsRemaining <= 0) {
      requireAuth();
      return;
    }
    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roger/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          use_knowledge_base: useKB,
          use_web_search: useWeb,
        }),
      });
      if (!res.ok) throw new Error('Failed to get response');
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response, sources: data.sources }]);
      if (!user) await trackQuestion();
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'The mind-bridge faltered. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const curate = async (article) => {
    setCurating(article.url || article.title);
    try {
      const res = await fetch(`${API_URL}/api/roger/rss/curate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: article.title, url: article.url, summary: article.summary }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Curation failed');
      setDocuments((prev) => [data.document, ...prev]);
      setTab('docs');
    } catch (e) {
      setRssNote(String(e.message || e));
    } finally {
      setCurating(null);
    }
  };

  const chatHeight = compact ? '340px' : '460px';

  const tabBtn = (id, Icon, label, testid) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setTab(id)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.5rem 1rem', borderRadius: '999px', cursor: 'pointer',
        background: tab === id ? 'rgba(0,255,136,0.15)' : 'transparent',
        border: `1px solid ${tab === id ? ACCENT : 'rgba(255,255,255,0.15)'}`,
        color: tab === id ? ACCENT : '#aaa', fontSize: '0.85rem', fontWeight: 600,
      }}
    >
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div data-testid="roger-interface">
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        {tabBtn('chat', MessageSquare, 'Chat with Roger', 'roger-tab-chat')}
        {tabBtn('rss', Rss, 'RSS Reading Room', 'roger-tab-rss')}
        {tabBtn('docs', BookOpen, 'Curated Documents', 'roger-tab-docs')}
      </div>

      {tab === 'chat' && (
        <>
          <Card data-testid="roger-chat-window" style={{ ...panelStyle, height: chatHeight, overflowY: 'auto', padding: '1.25rem', marginBottom: '0.75rem' }}>
            {messages.map((msg, i) => (
              <div key={`m-${i}`} style={{ display: 'flex', gap: '0.85rem', marginBottom: '1.25rem', alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: '36px', width: '36px', height: '36px', borderRadius: '50%',
                  background: msg.role === 'user' ? 'rgba(0,255,255,0.2)' : 'rgba(0,255,136,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `2px solid ${msg.role === 'user' ? CYAN : ACCENT}`,
                }}>
                  {msg.role === 'user' ? <User size={18} style={{ color: CYAN }} /> : <Cpu size={18} style={{ color: ACCENT }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.8rem', color: msg.role === 'user' ? CYAN : ACCENT, marginBottom: '0.35rem', fontWeight: 'bold' }}>
                    {msg.role === 'user' ? 'You' : 'Roger 5.1'}
                  </div>
                  <div style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  {msg.sources && msg.sources.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#777' }}>
                      Sources: {msg.sources.map((s, j) => (
                        <span key={`s-${j}`} style={{ marginRight: '0.6rem' }}>
                          {s.type === 'web_search' ? '🌐' : '📚'} {s.title || s.source}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', color: '#888' }}>
                <Loader className="animate-spin" size={18} />
                <span>Roger is thinking (Agent Zero verifying)...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </Card>

          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <label data-testid="roger-toggle-kb" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: useKB ? ACCENT : '#888', cursor: 'pointer' }}>
              <input type="checkbox" checked={useKB} onChange={(e) => setUseKB(e.target.checked)} />
              <Database size={13} /> Knowledge Base
            </label>
            <label data-testid="roger-toggle-web" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: useWeb ? ACCENT : '#888', cursor: 'pointer' }}>
              <input type="checkbox" checked={useWeb} onChange={(e) => setUseWeb(e.target.checked)} />
              <Globe size={13} /> Web Search
            </label>
          </div>

          <Card style={{ ...panelStyle, padding: '0.85rem' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="text"
                  data-testid="roger-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Roger anything..."
                  disabled={loading}
                  style={{ flex: 1, padding: '0.7rem', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(0,255,255,0.3)', borderRadius: '8px', color: '#fff', fontSize: '0.95rem' }}
                />
                <Button type="submit" data-testid="roger-chat-send" disabled={loading || !input.trim()} style={{ background: loading ? '#333' : `linear-gradient(135deg, ${CYAN}, ${ACCENT})`, color: '#0a0a0f', fontWeight: 700 }}>
                  {loading ? <Loader className="animate-spin" size={18} /> : <Send size={18} />}
                </Button>
              </div>
            </form>
          </Card>
        </>
      )}

      {tab === 'rss' && (
        <Card data-testid="roger-rss-panel" style={{ ...panelStyle, padding: '1.25rem' }}>
          <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.75rem' }}>
            Roger's reading room. Raw items never enter the Library — pull &amp; improve first. No spam on the shelves.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="text"
              data-testid="roger-rss-url-input"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              placeholder="Paste any RSS feed URL to read live..."
              style={{ flex: 1, padding: '0.6rem', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(0,255,255,0.3)', borderRadius: '8px', color: '#fff', fontSize: '0.85rem' }}
            />
            <Button data-testid="roger-rss-load" onClick={() => loadRss(feedUrl.trim() || undefined)} disabled={rssLoading} size="sm" style={{ background: `linear-gradient(135deg, ${CYAN}, ${ACCENT})`, color: '#0a0a0f', fontWeight: 700 }}>
              {rssLoading ? <Loader className="animate-spin" size={15} /> : <Rss size={15} />}
            </Button>
          </div>
          {rssNote && <p data-testid="roger-rss-note" style={{ color: '#ffb347', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{rssNote}</p>}
          <div style={{ maxHeight: compact ? '300px' : '420px', overflowY: 'auto' }}>
            {articles.map((a, i) => (
              <div key={`a-${i}`} data-testid="roger-rss-article" style={{ padding: '0.85rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#fff', marginBottom: '0.25rem' }}>{a.title}</div>
                <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '0.4rem' }}>{a.feed} {a.published && `· ${a.published}`}</div>
                <div style={{ fontSize: '0.83rem', color: '#bbb', marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: (a.summary || '').replace(/<[^>]+>/g, '').slice(0, 240) }} />
                <Button
                  size="sm"
                  data-testid="roger-rss-curate-btn"
                  onClick={() => curate(a)}
                  disabled={curating !== null}
                  style={{ background: 'rgba(255,215,0,0.15)', border: '1px solid #ffd700', color: '#ffd700', fontSize: '0.75rem' }}
                >
                  {curating === (a.url || a.title) ? <Loader className="animate-spin" size={13} /> : <Sparkles size={13} />}
                  &nbsp;Pull &amp; Improve → Document
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === 'docs' && (
        <Card data-testid="roger-docs-panel" style={{ ...panelStyle, padding: '1.25rem' }}>
          <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.75rem' }}>
            Documents pulled from the feeds and improved by Roger — ready for the Father's Library.
          </p>
          <div style={{ maxHeight: compact ? '320px' : '460px', overflowY: 'auto' }}>
            {documents.length === 0 && <p style={{ color: '#666', fontSize: '0.85rem' }}>No curated documents yet. Visit the RSS Reading Room and pull something valuable.</p>}
            {documents.map((d) => (
              <div key={d.id} data-testid="roger-curated-doc" style={{ padding: '0.9rem', marginBottom: '0.75rem', background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.25)', borderRadius: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.98rem', fontWeight: 700, color: '#ffd700' }}><FileText size={14} style={{ display: 'inline', marginRight: 4 }} />{d.title}</div>
                  <span style={{ fontSize: '0.68rem', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: '999px', padding: '2px 8px', whiteSpace: 'nowrap' }}>Ready for the Library</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: '#ccc', marginTop: '0.4rem', whiteSpace: 'pre-wrap', maxHeight: '160px', overflowY: 'auto' }}>{d.content}</div>
                {d.source_url && <a href={d.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.72rem', color: '#888' }}>source: {d.source_url}</a>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default RogerInterface;
